import React, { useMemo } from 'react';
import { Card, Col, Flex, Row, Typography } from 'antd';
import { startCase } from 'lodash';
import dayjs from '../../../dayjs';
import './BookingTemplate.css';
import { DATE_STRING } from '../../../model/schedule';
import { secsSinceMidnightToDayjs } from '../../../model/timeFilter';
import { useAppContext } from '../../../libs/contextLib';
import config from '../../../config';
import HorizontalTimeline from '../../../components/HorizontalTimeline';
const { Paragraph } = Typography;

const ConfirmationTemplate = ({ charter, allSchedules, operator, bookings = false, allCharterSchedules }) => {    
    const stopBreakdown = useMemo(() => {
        if (!charter?.itinerary?.length) {
            return [];
        }

        const times = []

        charter.itinerary.forEach(itinerary => {
            if (itinerary.stops.length > 0) {
                times.push({
                    firstStop: itinerary.stops[0],
                    firstStopTime: itinerary.stops[0]?.departSecs || null,
                    lastStop: itinerary.stops[itinerary.stops.length - 1],
                    lastStopTime: itinerary.stops[itinerary.stops.length - 1]?.departSecs || null,
                    middleStopsCount: itinerary.stops.length > 2 ? itinerary.stops.length - 2 : 0
                })
            }
        })

        return times
    }, [charter.itinerary]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value || 0);
    };

    if (!charter) {
        return <div>No charter data available</div>;
    }

    let startDate = charter.startDate;
    let startTime = charter.startTime;

    if (bookings) {
        startDate = charter.dates?.[0] || charter.startDate || 'TBC';
        const earliestStartTime = charter.itinerary.reduce((earliest, itinerary) => {
            itinerary.services.forEach(service => {
                const startTime = service.startTimeAsSecsSinceMidnight
                if (startTime < earliest) {
                    earliest = startTime;
                }
            });
            return earliest;
        }, Infinity);
        if (earliestStartTime === Infinity) {
            startTime = null;
        } else {
            startTime = earliestStartTime;
        }
    } else {
        if (!charter?.duties?.length) {
            startDate = charter.startDate;
            startTime = charter.startTime;
        } else {
            const firstDuty = charter.duties[0];
            const firstServiceRow = firstDuty.rows.find(row => row.type === 'Service');
            startDate = firstDuty?.getRunningDates(allSchedules, dayjs())?.[0];
            startTime = firstServiceRow?.time;
        }
    }

    const logo = `https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com/public/${operator?.operatorId}/logo.png?${Date.now()}`

    const quoteLineItems = charter.quoteLineItems || [];
    const confirmationDetails = charter.confirmationDetails || {};
    
    // Determine travel dates or schedule text based on charter type
    const isContractBooking = charter.charterType === "recurring";
    const scheduleName = isContractBooking && charter.scheduleId && allCharterSchedules ? allCharterSchedules[charter.scheduleId]?.scheduleName : null;

    return (
        <div className="print-pdf">
            <div className="quote-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px', paddingBottom: '25px', borderBottom: '2px solid #EFEDFD' }}>
                <div style={{ maxWidth: '45%' }}>
                    <div className="quote-title-section">
                        <h1 style={{ fontSize: 32, fontWeight: 600, color: '#333', marginBottom: 10 }}>BOOKING CONFIRMATION</h1>
                        <h2 className="pdf-title">{startCase(charter.name || '')}</h2>
                        <div className="quote-meta">
                            <div className="quote-number">
                                <label>Booking Reference</label>
                                <strong>{startCase(charter.bookingReference || '')}</strong>
                            </div>
                            <div className="quote-date">
                                <label>Confirmation Date</label>
                                <strong>{confirmationDetails.confirmedDate ? dayjs(confirmationDetails.confirmedDate).format(DATE_STRING) : dayjs().format(DATE_STRING)}</strong>
                            </div>
                            <div className="quote-date">
                                <label>Deposit Due Date</label>
                                <strong>{confirmationDetails.depositDueDate ? dayjs(confirmationDetails.depositDueDate).format(DATE_STRING) : '-'}</strong>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    operator &&
                    (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '20px', maxWidth: '55%' }}>
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#444', marginBottom: '5px' }}>{operator.operatorName}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorPostalAddress}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorPhone}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: 1.4 }}>{operator.operatorEmail}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '140px' }}>
                            <img id="operator-logo" src={logo}
                                style={{ width: '120px', height: 'auto', objectFit: 'contain'}}
                                alt={operator.operatorName}
                            />
                        </div>
                    </div>)
                }
            </div>

            <div className="quote-content">
                <Row gutter={[20, 20]}>
                    <Col xs={24}>
                        <Card title={''} className="card-main card-info">
                            <Row gutter={[20, 20]}>
                                <Col xs={24} sm={8}>
                                    <label>To</label>
                                    <strong>{startCase(charter.customer?.name || '')}</strong>
                                    <strong>{startCase(charter?.address1 || '')}</strong>
                                    <strong>{startCase(charter?.address2 || '')}</strong>
                                    <strong>{charter?.addressContact || ''}</strong>
                                </Col>
                                <Col xs={12} sm={8}>
                                    {isContractBooking ? (
                                        <>
                                            <label>Travel schedule</label>
                                            <strong>{scheduleName || "TBC"}</strong>
                                        </>
                                    ) : (
                                        <>
                                            <label>Travel dates</label>
                                            <div className="travel-dates-list">
                                                {charter.dates && charter.dates.length > 0 ? 
                                                    charter.dates.map((date, index) => (
                                                        <strong key={`date-${index}`} className="date-item">
                                                            {date?.format ? date.format(DATE_STRING) : ''}
                                                        </strong>
                                                    ))
                                                    : <strong>{startDate?.format ? startDate.format(DATE_STRING) : 'TBC'}</strong>
                                                }
                                            </div>
                                        </>
                                    )}
                                </Col>
                                <Col xs={12} sm={8}>
                                    <label>Passengers</label>
                                    <strong>{charter.passengerCount || 0}</strong>
                                </Col>
                                <Col xs={24} sm={24}>
                                    {confirmationDetails.specialInstructions && 
                                        <>
                                            <label>Special Instructions</label>
                                            <small>{(confirmationDetails.specialInstructions || '').split('\n').map((p, index) =>
                                                <Paragraph key={`desc-${index}`}>{p}</Paragraph>)}
                                            </small>
                                        </>
                                    }
                                </Col>
                                <Col xs={24} className='charter-steps' style={{ marginTop: '-1em' }}>
                                    <div>
                                        <label>Charter Itinerary</label>
                                    </div>
                                    {charter.itinerary.map((itinerary, index) => {
                                        // Prepare stops array for HorizontalTimeline
                                        const stops = itinerary.stops.map((stop, stopIdx) => ({
                                            ...stop,
                                            name: stop.stopName,
                                            time: stop.departSecs
                                                ? `${dayjs(charter.dates?.[0])
                                                    .add(Math.floor(stop.departSecs / (24 * 60 * 60)), 'days')
                                                    .format('DD/MM')} ${secsSinceMidnightToDayjs(stop.departSecs)?.format('HH:mm')}`
                                                : 'TBC',
                                        }));
                                        return (
                                            <HorizontalTimeline key={`custom-timeline-${index}`} stops={stops} />
                                        );
                                    })}
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card bordered={false} className="card-main pa-0">
                            <Row className="card-row-header align-items-center">
                                <Col xs={4} className="text-left">
                                    <span>NAME</span>
                                </Col>
                                <Col xs={8} className="text-left">
                                    <span>DESCRIPTION</span>
                                </Col>
                                <Col xs={3} className="text-right">
                                    <span>PRICE</span>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <span>QTY</span>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <span>DISCOUNT</span>
                                </Col>
                                <Col xs={3} className="text-right">
                                    <span>AMOUNT</span>
                                </Col>
                            </Row>
                            {quoteLineItems.map((line, index) => (
                                <Row key={`qline-${index}`} className="card-row-content border-full align-items-center">
                                    <Col xs={4} className="text-left">
                                        <strong>{startCase(line.name || '')}</strong>
                                    </Col>
                                    <Col xs={8} className="text-left">
                                        <span>{line.description || ''}</span>
                                    </Col>
                                    <Col xs={3} className="text-right">
                                        <span>{formatCurrency(line.unitPrice || 0)}</span>
                                    </Col>
                                    <Col xs={3} className="text-center">
                                        <span>{line.quantity || 0}</span>
                                    </Col>
                                    <Col xs={3} className="text-center">
                                        <span>{formatCurrency(line.discount || 0)}</span>
                                    </Col>
                                    <Col xs={3} className="text-right">
                                        <span>{formatCurrency(charter.calculateLineItemAmount(line))}</span>
                                    </Col>
                                </Row>
                            ))}
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>Line Item Total</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateSubTotal())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>SUBTOTAL</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateTotalAfterDiscount())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>GST (10%)</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateGST())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center totals-row final-total">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>TOTAL</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateTotal())}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>Deposit Required</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(confirmationDetails.depositAmount)}</strong>
                                </Col>
                            </Row>
                            <Row className="card-row-content border-full align-items-center">
                                <Col xs={20} className="text-right totals-label">
                                    <strong>Balance Due</strong>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <strong>{formatCurrency(charter.calculateTotal() - (confirmationDetails.depositAmount || 0))}</strong>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default React.memo(ConfirmationTemplate); 